import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const UIContext = createContext();

// Helper function to determine device size
const getDeviceSize = (width) => {
    if (width >= 1440) return "Desktop 1440";
    if (width >= 1200) return "Desktop 1200";
    if (width >= 810) return "Tablet";
    return "Phone";
};

export function UIProvider({ children }) {
    const [size, setSize] = useState(() => getDeviceSize(window.innerWidth));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);

    // Window resize handler
    useEffect(() => {
        const handleResize = () => {
            setSize(getDeviceSize(window.innerWidth));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Toast message handler
    const showToast = useCallback((message, type = 'info', duration = 3000) => {
        setToast({ message, type });
        setTimeout(() => setToast(null), duration);
    }, []);

    // Loading state handlers
    const startLoading = useCallback(() => {
        setIsLoading(true);
    }, []);

    const stopLoading = useCallback(() => {
        setIsLoading(false);
    }, []);

    // Error handlers
    const setErrorMessage = useCallback((message) => {
        setError(message);
        showToast(message, 'error');
    }, [showToast]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const value = {
        // Window size
        size,
        isDesktop: size.includes('Desktop'),
        isTablet: size === 'Tablet',
        isPhone: size === 'Phone',
        
        // Loading state
        isLoading,
        startLoading,
        stopLoading,
        
        // Error handling
        error,
        setErrorMessage,
        clearError,
        
        // Toast notifications
        toast,
        showToast,
    };

    return (
        <UIContext.Provider value={value}>
            {children}
            {/* Toast Component */}
            {toast && (
                <div className={`toast ${toast.type}`}>
                    {toast.message}
                </div>
            )}
        </UIContext.Provider>
    );
}

export function useUI() {
    const context = useContext(UIContext);
    if (!context) {
        throw new Error('useUI must be used within a UIProvider');
    }
    return context;
}